import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { ApiClientError } from '../api/apiClient';
import { completeGoogleAuth } from '../api/services';
import { ErrorDisplay } from '../components/common';
import { useAuthentication, useNotifications } from '../store/AppContext';
import { DESIGN_TOKENS } from '../styles/theme';
import type { AuthResponse } from '../types';
import { useI18n } from '../i18n';

const GOOGLE_AUTH_INTENT_STORAGE_KEY = 'qpro_google_auth_intent';
const googleAuthRequests = new Map<string, Promise<AuthResponse>>();

export function rememberGoogleAuthIntent(intent: 'login' | 'register') {
  try {
    sessionStorage.setItem(GOOGLE_AUTH_INTENT_STORAGE_KEY, intent);
  } catch {
    // Session storage is optional; the callback can still complete login.
  }
}

function readGoogleAuthIntent(): 'login' | 'register' {
  try {
    const storedIntent = sessionStorage.getItem(GOOGLE_AUTH_INTENT_STORAGE_KEY);
    sessionStorage.removeItem(GOOGLE_AUTH_INTENT_STORAGE_KEY);
    return storedIntent === 'register' ? 'register' : 'login';
  } catch {
    return 'login';
  }
}

function getCallbackParam(locationSearch: string, paramName: string): string | null {
  const paramSources = [
    locationSearch,
    window.location.search,
    window.location.hash.includes('?')
      ? window.location.hash.slice(window.location.hash.indexOf('?'))
      : '',
  ];

  for (const paramSource of paramSources) {
    const paramValue = new URLSearchParams(paramSource).get(paramName);
    if (paramValue) return paramValue;
  }

  return null;
}

function completeGoogleAuthOnce(accessToken: string): Promise<AuthResponse> {
  const existingRequest = googleAuthRequests.get(accessToken);
  if (existingRequest) return existingRequest;

  const authRequest = completeGoogleAuth(accessToken);
  googleAuthRequests.set(accessToken, authRequest);
  return authRequest;
}

function getGoogleAuthErrorMessage(error: unknown, failedPrefix: string, failedGeneric: string): string {
  if (error instanceof ApiClientError) {
    return `${failedPrefix} ${error.apiError.message}`;
  }

  if (error instanceof Error) {
    return `${failedPrefix} ${error.message}`;
  }

  return failedGeneric;
}

export default function AuthCallbackPage() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { provider } = useParams();
  const { loginUser: setUserLoggedIn } = useAuthentication();
  const { showNotification } = useNotifications();
  const [error, setError] = useState<string | null>(null);

  const accessToken = useMemo(
    () => getCallbackParam(location.search, 'access_token'),
    [location.search]
  );
  const providerError = useMemo(
    () => getCallbackParam(location.search, 'error'),
    [location.search]
  );

  useEffect(() => {
    let isMounted = true;

    async function finishGoogleAuthentication() {
      if (provider !== 'google') {
        setError(t('callback.unsupportedProvider'));
        return;
      }

      if (providerError) {
        setError(t('callback.cancelled'));
        return;
      }

      if (!accessToken) {
        setError(t('callback.missingToken'));
        return;
      }

      try {
        const authResponse = await completeGoogleAuthOnce(accessToken);
        if (!isMounted) return;

        setUserLoggedIn(authResponse.user, authResponse.jwt);
        showNotification(t('callback.success'), 'success');
        const authIntent = readGoogleAuthIntent();
        navigate(authIntent === 'register' ? '/first-steps' : '/account', {
          replace: true,
        });
      } catch (error) {
        if (isMounted) {
          setError(getGoogleAuthErrorMessage(error, t('callback.failedPrefix'), t('callback.failedGeneric')));
        }
      }
    }

    finishGoogleAuthentication();

    return () => {
      isMounted = false;
    };
  }, [accessToken, navigate, provider, providerError, setUserLoggedIn, showNotification]);

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
        py: 6,
        px: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 4 } }}>
        <CardContent sx={{ p: 0, textAlign: 'center' }}>
          {error ? (
            <>
              <ErrorDisplay message={error} />
              <Button component={RouterLink} to="/login" variant="contained">
                {t('callback.backToLogin')}
              </Button>
            </>
          ) : (
            <>
              <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary, mb: 3 }} />
              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('callback.finishingTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('callback.finishingDescription')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
